import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { blenderCss } from '@cld/blender';
import { globalTheme, useMixin } from '@cld/component-theme';
import { FoundationContextProvider, Size, SurfaceContextProvider } from '@cld/design-system-foundations';
import { Check } from '@cld/icon-next';
import { useItemContext } from './ItemContext';
import { itemLayoutSizeVariant, itemTheme, ItemThemeProps, onelinerSizeVariant, twolinerSizeVariant } from './theme';
import { SupportedLayoutTypographySize } from './types';

export interface ItemLayoutProps {
  leftSlot?: ReactNode;
  rightSlot?: ReactNode;
  secondaryContent?: ReactNode;
}

export const ItemLayout: FC<ItemLayoutProps> = ({ leftSlot, rightSlot, secondaryContent, children, ...rest }) => {
  const { size, isSelected, showCheckIconWhenSelected, selectionVariant, variant } = useItemContext();
  const slotSize = useMixin(itemTheme.layout.normal.sizeVariant[size].slotSize) as Size;
  const surfaceTone = isSelected ? selectionVariant : 'subtle';

  const leftSlotContent = leftSlot && (
    <SlotWrapper size={size}>
      <SurfaceContextProvider variant={variant} tone={surfaceTone}>
        <FoundationContextProvider size={slotSize}>{leftSlot}</FoundationContextProvider>
      </SurfaceContextProvider>
    </SlotWrapper>
  );

  const mainContent = secondaryContent ? (
    <TwolinerWrapper size={size}>
      <Line1Wrapper size={size} data-test="item--primary-content">
        {children}
      </Line1Wrapper>
      <Line2Wrapper size={size} data-test="item--secondary-content">
        {secondaryContent}
      </Line2Wrapper>
    </TwolinerWrapper>
  ) : (
    <OnelinerWrapper data-test="item--primary-content" size={size}>
      {children}
    </OnelinerWrapper>
  );

  const rightSlotContent = rightSlot && (
    <RightSlotWrapper size={size}>
      <SurfaceContextProvider variant={variant} tone={surfaceTone}>
        <FoundationContextProvider size={slotSize}>{rightSlot}</FoundationContextProvider>
      </SurfaceContextProvider>
    </RightSlotWrapper>
  );

  const checkIcon = isSelected && showCheckIconWhenSelected && <SelectedIcon size="sm" data-test="check-icon" />;

  return (
    <Root size={size} {...rest}>
      {leftSlotContent}
      {mainContent}
      {rightSlotContent}
      {checkIcon}
    </Root>
  );
};

const Root = styled.div`
  display: flex;
  align-items: center;
  text-align: left; // some inheritable styles might be transferred via popover rendered inside a button for example.
  > :first-child {
    margin-left: ${itemLayoutSizeVariant((variant) => variant.horizontalMargin)};
  }
  > * {
    margin-right: ${itemLayoutSizeVariant((variant) => variant.horizontalMargin)};
  }
`;

const SlotWrapper = styled.div<ItemThemeProps>`
  flex-shrink: 0;
  line-height: 0;
  display: flex;
  align-items: center;
  & > :not(:last-child) {
    margin-right: ${globalTheme.spacing.xs};
  }
  margin-top: ${itemLayoutSizeVariant((variant) => variant.slotVerticalMargin)};
  margin-bottom: ${itemLayoutSizeVariant((variant) => variant.slotVerticalMargin)};
`;

const RightSlotWrapper = styled(SlotWrapper)`
  height: 0; //buttons are bigger than foundation expects, for now we don't count them in height
`;

const Line1Wrapper = styled.div`
  display: flex;
  flex: 1;
  ${twolinerSizeVariant((branch) => blenderCss.typographyFromCssVariables(branch.typographySize.line1 as SupportedLayoutTypographySize))};
`;

const Line2Wrapper = styled.div`
  ${twolinerSizeVariant((branch) => blenderCss.typographyFromCssVariables(branch.typographySize.line2 as SupportedLayoutTypographySize))};
  margin-top: ${twolinerSizeVariant((branch) => branch.gap)};
`;

const TwolinerWrapper = styled.div`
  min-width: 0;
  flex: 1;
  margin-top: ${twolinerSizeVariant((branch) => branch.verticalMargin)};
  margin-bottom: ${twolinerSizeVariant((branch) => branch.verticalMargin)};
`;

const OnelinerWrapper = styled.div`
  min-width: 0;
  flex: 1;
  ${onelinerSizeVariant((branch) => blenderCss.typographyFromCssVariables(branch.typographySize as SupportedLayoutTypographySize))};
  margin-top: ${onelinerSizeVariant((branch) => branch.verticalMargin)};
  margin-bottom: ${onelinerSizeVariant((branch) => branch.verticalMargin)};
`;

const SelectedIcon = styled(Check)`
  margin-left: auto;
  flex-shrink: 0;
  margin-right: ${globalTheme.spacing.sm};
`;
